import {
  FormField,
  FoundAddressCard,
  getNestedFieldError,
  Input,
  NumberInput,
} from '@maia/ui';
import { useFieldContext } from '../dynamic-form';
import { ComponentProps, FormEvent, useEffect, useId, useState } from 'react';
import { formatAddress } from '@maia/utils';

type PostcodeLookupProps = {
  showAdditionalFields?: boolean;
  showFoundAddress?: boolean;
  inputOverrides?: ComponentProps<typeof NumberInput>;
} & Omit<ComponentProps<typeof FormField>, 'inputSlot'>;

export function PostcodeLookup(props: PostcodeLookupProps) {
  const id = useId();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { showAdditionalFields = false, showFoundAddress = false } = props;
  const field = useFieldContext<{
    street: string;
    number?: number;
    appendix: string;
    city: string;
    postalCode: string;
  }>();

  const postalCodeErrors = getNestedFieldError(field, 'postalCode');
  const numberErrors = getNestedFieldError(field, 'number');
  const appendixErrors = getNestedFieldError(field, 'appendix');

  useEffect(() => {
    if (showFoundAddress) {
      setIsEditing(false);
    }
  }, [showFoundAddress]);

  return (
    <>
      <FormField
        label="Postcode & Huisnummer"
        className="col-span-full"
        wrapperField="div"
        error={postalCodeErrors || numberErrors || appendixErrors}
        htmlFor={`${id}-postal-code`}
        inputSlot={
          <div className="col-span-full grid grid-cols-6 gap-16">
            <Input
              type="text"
              id={`${id}-postal-code`}
              name={`${id}-postal-code`}
              fieldSize="large"
              placeholder="____ __"
              className="col-span-4"
              value={field.store.state.value.postalCode}
              onChange={(event) => {
                field.handleChange({
                  ...field.store.state.value,
                  postalCode: event.target.value,
                });
              }}
            />

            <NumberInput
              fieldSize="large"
              placeholder="123"
              id={`${id}-house-number`}
              name={`${id}-house-number`}
              value={field.store.state.value.number}
              onInput={(event: FormEvent<HTMLInputElement>) => {
                field.handleChange({
                  ...field.store.state.value,
                  number: parseInt(event.currentTarget?.value) ?? undefined,
                });
              }}
              onChange={(event) => {
                field.handleChange({
                  ...field.store.state.value,
                  number: event,
                });
              }}
            />

            <Input
              type="text"
              fieldSize="large"
              placeholder="Toev."
              id={`${id}-appendix`}
              name={`${id}-appendix`}
              value={field.store.state.value.appendix}
              onChange={(event) => {
                field.handleChange({
                  ...field.store.state.value,
                  appendix: event.target.value,
                });
              }}
            />
          </div>
        }
      />

      {showFoundAddress &&
        !isEditing &&
        typeof field.store.state.value.number === 'number' && (
          <FoundAddressCard
            address={formatAddress(field.store.state.value)}
            className="col-span-full"
            onEdit={() => setIsEditing(true)}
            onDelete={() => {
              field.setValue({
                postalCode: '',
                number: null,
                appendix: '',
                street: '',
                city: '',
              });
            }}
          />
        )}

      {(showAdditionalFields || isEditing) && (
        <FormField
          label="Stad"
          className="col-span-full"
          error={getNestedFieldError(field, 'city')}
          inputSlot={
            <Input
              type="text"
              fieldSize="large"
              placeholder="Stad"
              value={field.store.state.value.city}
              onChange={(event) => {
                field.handleChange({
                  ...field.store.state.value,
                  city: event.target.value,
                });
              }}
            />
          }
        />
      )}

      {(showAdditionalFields || isEditing) && (
        <FormField
          label="Straat"
          className="col-span-full"
          error={getNestedFieldError(field, 'street')}
          inputSlot={
            <Input
              type="text"
              fieldSize="large"
              placeholder="Straat"
              value={field.store.state.value.street}
              onChange={(event) => {
                field.handleChange({
                  ...field.store.state.value,
                  street: event.target.value,
                });
              }}
            />
          }
        />
      )}
    </>
  );
}
